import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DriverGroupDto } from '@cargovibe/shared-types';
import { useDriverGroupEditor } from '@/modules/driver-groups/hooks/useDriverGroupEditor';
import { getDriverGroupNameError } from '@/modules/driver-groups/getDriverGroupNameError';
import { completeDriverGroupCreation } from '@/modules/driver-groups/effects/completeDriverGroupCreation';
import { completeDriverGroupDeletion } from '@/modules/driver-groups/effects/completeDriverGroupDeletion';

interface DriverGroupWorkflow {
  editor: ReturnType<typeof useDriverGroupEditor>;
  isCreateGroupOpen: boolean;
  groupPendingDeletion: DriverGroupDto | null;
  groupNameError: string;
  toastMessage: string;
  openCreateGroup: () => void;
  closeCreateGroup: () => void;
  handleGroupCreated: (group: DriverGroupDto) => void;
  startEditingActiveGroup: () => void;
  saveGroup: () => void;
  requestDeleteGroup: () => void;
  closeDeleteGroup: () => void;
  handleGroupDeleted: () => void;
  dismissToast: () => void;
}

export function useDriverGroupWorkflow(driverGroups: DriverGroupDto[]): DriverGroupWorkflow {
  const { t } = useTranslation();
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState<boolean>(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const editor: ReturnType<typeof useDriverGroupEditor> = useDriverGroupEditor(driverGroups);

  const openCreateGroup = (): void => {
    setIsCreateGroupOpen(true);
  };

  const closeCreateGroup = (): void => {
    setIsCreateGroupOpen(false);
  };

  const handleGroupCreated = (group: DriverGroupDto): void => {
    completeDriverGroupCreation({
      group,
      createdMessage: t('driverGroups.groupCreated'),
      closeCreateDialog: closeCreateGroup,
      startEditing: editor.startEditing,
      showToast: setToastMessage,
    });
  };

  const startEditingActiveGroup = (): void => {
    if (editor.activeGroup) editor.startEditing(editor.activeGroup);
  };

  const saveGroup = (): void => {
    editor.save((): void => {
      setToastMessage(t('driverGroups.groupUpdated'));
    });
  };

  const requestDeleteGroup = (): void => {
    setIsDeletingGroup(true);
  };

  const closeDeleteGroup = (): void => {
    setIsDeletingGroup(false);
  };

  const handleGroupDeleted = (): void => {
    completeDriverGroupDeletion({
      deletedMessage: t('driverGroups.groupDeleted'),
      closeDeleteDialog: closeDeleteGroup,
      forgetGroup: editor.forgetGroup,
      showToast: setToastMessage,
    });
  };

  const dismissToast = (): void => {
    setToastMessage('');
  };

  return {
    editor,
    isCreateGroupOpen,
    groupPendingDeletion: isDeletingGroup ? editor.activeGroup : null,
    groupNameError: editor.isNameValid
      ? getDriverGroupNameError(editor.saveError, t)
      : t('driverGroups.errGroupName'),
    toastMessage,
    openCreateGroup,
    closeCreateGroup,
    handleGroupCreated,
    startEditingActiveGroup,
    saveGroup,
    requestDeleteGroup,
    closeDeleteGroup,
    handleGroupDeleted,
    dismissToast,
  };
}
